import React from 'react';
import { Provider } from 'react-redux';
import { Router } from 'react-router';

import App from 'layouts/App';

import { getBanner } from 'actions/banner';
import { getRoom } from 'actions/room';
import { pageChanging, pageChanged, loadingRatio } from 'actions/global';
import { getUser, setIgAccessToken } from 'actions/user';
import { getCollect } from 'actions/collect';

import {
	routeMapLanguage,
	changeLng,
	isLanguageSupportWithRoute,
	languageMapRoute,
} from 'util/i18n';
import { withLoader, withAsyncEnterLoader } from 'util/routeHelper';
import { sleep } from 'util/helper';

import MapRoute from './Map';
import RoomRoute from './Room';
import ApartmentRoute from './Apartment';
import AboutRoute from './About';
import MediaRoute from './Media';
import TeamRoute from './Team';
import ContactRoute from './Contact';
import SpaceRoute from './Space';
import ColivingRoute from './Coliving';
import SiteMapRoute from './SiteMap';
import ServiceRoute from './Service';
import Page404Route from './Page404';
import UserRoute from './User';
import ForgetRoute from './Forget';
import VerifyRoute from './Verify';
import LoginRoute from './Login';
import SignupRoute from './Signup';
import PrivacyRoute from './Privacy';
import TermsRoute from './Terms';

const createRoutes = store => ({
	path: '/',
	component: App,
	indexRoute: {
		getComponent: withLoader(
			(nextState, cb) =>
				require.ensure(
					[],
					require => {
						const component = require('./Home').default;
						cb(null, component);
					},
					'Home',
				),
			store,
			'HOME',
		),
		onEnter: withAsyncEnterLoader(
			async (_, __, updateRatio) => {
				await Promise.all([store.dispatch(getBanner()), store.dispatch(getRoom())]);
				updateRatio(0.9);
			},
			store,
			'ENTER_HOME',
		),
	},
	onEnter: withAsyncEnterLoader(
		async ({ location: { pathname } }, _, updateRatio) => {
			updateRatio(0.5);
			const { auth: { token: { accessToken } }, i18n: { lan } } = store.getState();

			if (
				accessToken !== '' &&
				!pathname.includes('user/settings') &&
				!pathname.includes('user/interview')
			) {
				await store.dispatch(getUser());

				if (pathname.includes(`${languageMapRoute(lan)}/room`)) {
					await store.dispatch(getCollect());
				}
			}

			updateRatio(0.8);
		},
		store,
		'ENTER_APP',
	),
	onChange: async (prevState, nextState, replace, cb) => {
		const { location: { pathname: prevPathname } } = prevState;
		const { location: { pathname: nextPathname } } = nextState;

		const effectLoading = prevPathname !== nextPathname;

		if (effectLoading) {
			store.dispatch(pageChanging('CHANGE_APP'));
			store.dispatch(loadingRatio(0, 'CHANGE_APP'));
			await sleep(300);
			cb();
			store.dispatch(loadingRatio(1, 'CHANGE_APP'));
			store.dispatch(pageChanged('CHANGE_APP'));
		} else {
			cb();
		}
	},
	childRoutes: [
		{
			path: 'auth',
			indexRoute: {
				component: null,
			},
			onEnter: withAsyncEnterLoader(
				async ({ location: { query: { code = '' } } }, _, updateRatio) => {
					updateRatio(0.8);

					await store.dispatch(setIgAccessToken(code));
				},
				store,
				'ENTER_AUTH',
			),
		},
		{
			path: ':lang',
			indexRoute: {
				getComponent: withLoader(
					(nextState, cb) =>
						require.ensure(
							[],
							require => {
								const component = require('./Home').default;
								cb(null, component);
							},
							'Home',
						),
					store,
					'LANGUAGE_HOME',
				),
				onEnter: withAsyncEnterLoader(
					async (_, __, updateRatio) => {
						await Promise.all([store.dispatch(getBanner()), store.dispatch(getRoom())]);
						updateRatio(0.9);
					},
					store,
					'ENTER_LANGUAGE_HOME',
				),
			},
			onEnter: withAsyncEnterLoader(
				async ({ params: { lang } }, replace) => {
					const { i18n: { lan } } = store.getState();

					if (!isLanguageSupportWithRoute(lang)) {
						replace(`/${languageMapRoute(lan)}/404`);
						return;
					}

					const newLang = routeMapLanguage(lang);

					if (newLang !== lan) {
						changeLng(newLang);
					}
				},
				store,
				'ENTER_LANGUAGE_HOME',
			),
			childRoutes: [
				MapRoute(store),
				RoomRoute(store),
				ApartmentRoute(store),
				AboutRoute(store),
				MediaRoute(store),
				TeamRoute(store),
				ContactRoute(store),
				SpaceRoute(store),
				ColivingRoute(store),
				SiteMapRoute(store),
				ServiceRoute(store),
				UserRoute(store),
				ForgetRoute(store),
				VerifyRoute(store),
				LoginRoute(store),
				SignupRoute(store),
				PrivacyRoute(store),
				TermsRoute(store),
				Page404Route(store),
			],
		},
	],
});

const Routes = ({ store, history }) => (
	<Provider store={store}>
		<Router
			key={Math.random()}
			onUpdate={() => window.scrollTo(0, 0)}
			history={history}
			routes={createRoutes(store)}
		/>
	</Provider>
);

export default Routes;



// WEBPACK FOOTER //
// ./src/routes/index.js